import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextImprovementService, ImprovementType } from '../services/textImprovementService';
import { OpenRouterConfig } from './OpenRouterConfig';
import { useLanguage } from '../hooks/useLanguage';

interface MagicWandProps {
  content: string;
  onContentImproved: (improvedContent: string) => void;
  onStart?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
  className?: string;
  openRouterConfig?: OpenRouterConfig;
}

export const MagicWand: React.FC<MagicWandProps> = ({
  content,
  onContentImproved,
  onStart,
  onEnd,
  disabled = false,
  className = '',
  openRouterConfig
}) => {
  const [isImproving, setIsImproving] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { language } = useLanguage();

  const improvementOptions = [
    { 
      type: 'grammar' as ImprovementType, 
      icon: '✓'
    },
    { 
      type: 'clarity' as ImprovementType, 
      icon: '💡'
    },
    { 
      type: 'structure' as ImprovementType, 
      icon: '🏗️'
    },
    { 
      type: 'tone' as ImprovementType, 
      icon: '🎭'
    },
    { 
      type: 'conciseness' as ImprovementType, 
      icon: '✂️'
    },
    { 
      type: 'translate' as ImprovementType, 
      icon: '🌍'
    }
  ];

  const handleImprove = async (improvementType: ImprovementType = 'general') => {
    if (!content.trim() || disabled || !openRouterConfig) {
      if (!openRouterConfig) {
        setError(t('magicWand.error.configureApi'));
        return;
      }
      return;
    }

    setIsImproving(true);
    setShowOptions(false);
    setError(null);
    onStart?.();

    try {
      const improvementService = new TextImprovementService(openRouterConfig);
      const result = await improvementService.improveText(content, improvementType, language);
      onContentImproved(result.improvedText);
    } catch (error) {
      console.error('Erro ao melhorar texto:', error);
      setError(error instanceof Error ? error.message : t('magicWand.error.improving'));
    } finally {
      setIsImproving(false);
      onEnd?.();
    }
  };

  const isPortuguese = language === 'pt-BR';

  return (
    <div className={`relative ${className}`}>
      {/* Botão principal da varinha */}
      <button
        onClick={() => content.trim() ? setShowOptions(!showOptions) : handleImprove()}
        disabled={disabled || !content.trim() || !openRouterConfig}
        className={`
          group relative p-2 rounded-lg transition-all duration-200
          ${disabled || !content.trim() || !openRouterConfig
            ? 'text-ghost-300 cursor-not-allowed' 
            : 'text-ghost-400 hover:text-purple-600 hover:bg-purple-50'
          }
          ${isImproving ? 'animate-pulse' : ''}
        `}
        title={t('magicWand.tooltip')}
        aria-label={t('magicWand.ariaLabel')}
      >
        {isImproving ? (
          <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M7.5 5.6L10 7L8.6 4.5C8.2 3.9 7.5 3.9 7.1 4.5L7.5 5.6M19.5 15.4L22 14L20.6 16.5C20.2 17.1 19.5 17.1 19.1 16.5L19.5 15.4M22 5.6L20.6 7.5C20.2 8.1 19.5 8.1 19.1 7.5L19.5 6.4L22 5.6M7.5 18.4L10 17L8.6 19.5C8.2 20.1 7.5 20.1 7.1 19.5L7.5 18.4M12 12L15.4 8.6C16.2 7.8 17.8 7.8 18.6 8.6C19.4 9.4 19.4 11 18.6 11.8L12 12M12 12L8.6 15.4C7.8 16.2 6.2 16.2 5.4 15.4C4.6 14.6 4.6 13 5.4 12.2L12 12M12 12L15.4 15.4C16.2 16.2 16.2 17.8 15.4 18.6C14.6 19.4 13 19.4 12.2 18.6L12 12M12 12L8.6 8.6C7.8 7.8 7.8 6.2 8.6 5.4C9.4 4.6 11 4.6 11.8 5.4L12 12Z" />
          </svg>
        )}
        
        {/* Efeito de brilho quando hover */}
        <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-r from-purple-400/20 to-pink-400/20 animate-pulse" />
      </button>

      {/* Menu de opções */}
      {showOptions && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white/95 backdrop-blur-md border border-ghost-200 rounded-lg shadow-lg z-50 animate-float-up">
          <div className="p-3 border-b border-ghost-100">
            <h3 className="text-sm font-medium text-ghost-800">
              ✨ {t('magicWand.title')}
            </h3>
            <p className="text-xs text-ghost-500 mt-1">
              {t('magicWand.subtitle')}
            </p>
          </div>
          
          <div className="p-2">
            {/* Opção de melhoria geral */}
            <button
              onClick={() => handleImprove()}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-sm hover:bg-purple-50 rounded-lg transition-colors duration-150"
            >
              <span className="text-lg">🪄</span>
              <div className="flex-1">
                <div className="font-medium text-ghost-800">
                  {t('magicWand.options.general.title')}
                </div>
                <div className="text-xs text-ghost-500">
                  {t('magicWand.options.general.description')}
                </div>
              </div>
            </button>
            
            <div className="h-px bg-ghost-100 my-2" />
            
            {/* Opções específicas */}
            {improvementOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => handleImprove(option.type)}
                className="w-full flex items-center space-x-3 px-3 py-2 text-left text-sm hover:bg-ghost-50 rounded-lg transition-colors duration-150"
              >
                <span className="text-base">{option.icon}</span>
                <div className="flex-1">
                  <div className="font-medium text-ghost-800">
                    {t(`magicWand.options.${option.type}.title`)}
                  </div>
                  <div className="text-xs text-ghost-500">
                    {t(`magicWand.options.${option.type}.description`)}
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          <div className="p-2 border-t border-ghost-100">
            <button
              onClick={() => setShowOptions(false)}
              className="text-xs text-ghost-400 hover:text-ghost-600 transition-colors duration-150"
            >
              {t('magicWand.close')}
            </button>
          </div>
        </div>
      )}
      
      {/* Overlay para fechar menu quando clicar fora */}
      {showOptions && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowOptions(false)}
        />
      )}
      
      {/* Error display */}
      {error && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg z-50">
          <div className="flex items-start space-x-2">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs text-red-600 hover:text-red-800 mt-1"
              >
                {t('magicWand.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};